import { Star } from 'lucide-react';

const REQUIRED_DOT = <span className="text-rose-500 ml-0.5">*</span>;

/**
 * Lightweight, non-interactive preview of a single field for the
 * builder canvas. Renders the field LIKE the candidate would see it but
 * without focus traps, validation, or stateful inputs.
 */
export const FieldPreview = ({ field }) => {
  const label = (
    <label className="text-sm font-medium text-foreground block mb-1.5">
      {field.label || <span className="text-muted-foreground italic">(no label)</span>}
      {field.required && REQUIRED_DOT}
    </label>
  );
  const help = field.help_text && (
    <p className="text-xs text-muted-foreground mt-1">{field.help_text}</p>
  );

  const placeholder = field.placeholder || '';

  switch (field.field_type) {
    case 'heading':
      return (
        <h3 className="font-heading text-xl text-foreground py-1">
          {field.label || 'Section heading'}
        </h3>
      );
    case 'paragraph':
      return (
        <p className="text-sm text-muted-foreground whitespace-pre-wrap py-1">
          {field.label || 'Helper text — describe a section, instructions, or context.'}
        </p>
      );
    case 'divider':
      return <hr className="border-t border-border my-2" />;
    case 'textarea':
      return (
        <div>
          {label}
          <div className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm text-muted-foreground">
            {placeholder || 'Long answer…'}
          </div>
          {help}
        </div>
      );
    case 'dropdown':
      return (
        <div>
          {label}
          <div className="w-full h-9 rounded-md border border-input bg-background px-3 py-2 text-sm text-muted-foreground flex items-center justify-between">
            <span>{placeholder || 'Select…'}</span>
            <span className="text-xs">▾</span>
          </div>
          {help}
        </div>
      );
    case 'multi_select':
      return (
        <div>
          {label}
          <div className="space-y-1">
            {(field.options || []).slice(0, 3).map((o, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <div className="w-4 h-4 border border-input rounded" />
                <span>{o.label}</span>
              </div>
            ))}
            {!field.options?.length && (
              <p className="text-xs text-muted-foreground italic">No options yet</p>
            )}
          </div>
          {help}
        </div>
      );
    case 'radio':
      return (
        <div>
          {label}
          <div className="space-y-1">
            {(field.options || []).slice(0, 3).map((o, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <div className="w-4 h-4 border border-input rounded-full" />
                <span>{o.label}</span>
              </div>
            ))}
            {!field.options?.length && (
              <p className="text-xs text-muted-foreground italic">No options yet</p>
            )}
          </div>
          {help}
        </div>
      );
    case 'checkbox':
      return (
        <div className="flex items-start gap-2">
          <div className="w-4 h-4 border border-input rounded mt-0.5" />
          <span className="text-sm">{field.label || 'I agree…'}{field.required && REQUIRED_DOT}</span>
        </div>
      );
    case 'date':
      return (
        <div>
          {label}
          <div className="w-full h-9 rounded-md border border-input bg-background px-3 py-2 text-sm text-muted-foreground">dd/mm/yyyy</div>
          {help}
        </div>
      );
    case 'time':
      return (
        <div>
          {label}
          <div className="w-full h-9 rounded-md border border-input bg-background px-3 py-2 text-sm text-muted-foreground">--:--</div>
          {help}
        </div>
      );
    case 'name':
      return (
        <div>
          {label}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="w-full h-9 rounded-md border border-input bg-background px-3 py-2 text-sm text-muted-foreground">First</div>
              <p className="text-[10px] text-muted-foreground mt-0.5">First name</p>
            </div>
            <div>
              <div className="w-full h-9 rounded-md border border-input bg-background px-3 py-2 text-sm text-muted-foreground">Last</div>
              <p className="text-[10px] text-muted-foreground mt-0.5">Last name</p>
            </div>
          </div>
          {help}
        </div>
      );
    case 'rating':
      return (
        <div>
          {label}
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} className="w-6 h-6" stroke="#cbd5e1" fill="transparent" strokeWidth={1.5} />
            ))}
          </div>
          {help}
        </div>
      );
    case 'signature':
      return (
        <div>
          {label}
          <div className="w-full h-24 rounded-md border border-dashed border-input bg-slate-50 flex items-center justify-center">
            <span className="text-xs text-muted-foreground italic">Signature pad</span>
          </div>
          {help}
        </div>
      );
    case 'file_upload':
      return (
        <div>
          {label}
          <div className="w-full px-4 py-3 rounded-md border-2 border-dashed border-input bg-slate-50 text-center text-xs text-muted-foreground">
            Click to upload
          </div>
          {help}
        </div>
      );
    case 'email':
    case 'phone':
    case 'number':
    case 'url':
    case 'text':
    default:
      return (
        <div>
          {label}
          <div className="w-full h-9 rounded-md border border-input bg-background px-3 py-2 text-sm text-muted-foreground">
            {placeholder || `${field.field_type === 'email' ? 'name@example.com' : field.field_type === 'url' ? 'https://example.com' : 'Short answer…'}`}
          </div>
          {help}
        </div>
      );
  }
};
